import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Chip,
  Card,
  CardMedia,
  CardContent,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Alert
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { alpha } from '@mui/material/styles';
import { storage } from '../../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { db } from '../../firebase';
import { collection, doc, setDoc, getDocs, deleteDoc, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { useData } from '../DataProvider';
import { useAuth } from '../../tenantloader/AuthContextProvider';
import { INSPECTORCLICK_API_BASE_URL } from '../../appConfig';

// Component to load and display images from Firebase storage
const AutoInspectImage = ({ imagePath, appInfo, alt = "AutoInspect image", width = 250, height = 180, shouldIgnore = false }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFullSize, setShowFullSize] = useState(false);

  useEffect(() => {
    let cancelled = false;
    
    const loadImage = async () => {
      try {
        if (cancelled) return;
        
        setLoading(true);
        setError(null);
        
        if (!appInfo || !appInfo.clickimagesPath || !imagePath) {
          if (!cancelled) {
            setLoading(false);
            setError('Image path not available');
          }
          return;
        }
        
        // Extract just the filename from the path if it's a full path
        // Handle both full paths and just filenames
        // Support both forward slashes (/) and backslashes (\)
        let fileName = imagePath;
        let firebaseImagePath;
        
        // Check if imagePath is already a full Firebase storage path (starts with "Inspect/")
        // For EvidenceFileNames, these might be complete paths that should be used directly
        // OR they might need the filename extracted and used with clickimagesPath
        // Try both approaches: first use the full path directly, then fallback to extraction
        if (imagePath.startsWith('Inspect/')) {
          // First, try using the full path directly (it might already be correct)
          // Ensure it has .jpg extension
          const directPath = imagePath.replace(/\.(jpg|png|jpeg)$/i, '') + '.jpg';
          
          // Store both paths - we'll try direct first, then extracted if that fails
          firebaseImagePath = directPath;
          
          // Also prepare the extracted filename version as a fallback
          let extractedName = imagePath.replace(/\\/g, '/').split('/').pop();
          extractedName = extractedName.replace(/\.(jpg|png|jpeg)$/i, '');
          // Remove "watch_" prefix if present (it's part of the path, not the filename)
          if (extractedName.startsWith('watch_')) {
            extractedName = extractedName.substring(6); // Remove "watch_" (6 characters)
          }
        } else {
          // It's a relative path or just filename - extract filename and use with clickimagesPath
          // If it's a path, extract just the filename (handle both / and \)
          if (fileName.includes('/') || fileName.includes('\\')) {
            // Normalize to forward slashes first, then split
            fileName = fileName.replace(/\\/g, '/').split('/').pop();
          }
          
          // Remove extension if present (jpg, png, jpeg)
          fileName = fileName.replace(/\.(jpg|png|jpeg)$/i, '');
          
          // Ensure we have a valid filename
          if (!fileName || fileName.trim() === '') {
            if (!cancelled) {
              setLoading(false);
              setError('Invalid image filename');
            }
            return;
          }
          
          firebaseImagePath = `${appInfo.clickimagesPath}/${fileName}.jpg`;
        }

        try {
          const imageRef = ref(storage, firebaseImagePath);
          const url = await getDownloadURL(imageRef);
          if (url && !cancelled) {
            setImageUrl(url);
          }
        } catch (error) {
          if (cancelled) return;
          
          // If it's a full path that failed, try extracting filename and using with clickimagesPath
          if (imagePath.startsWith('Inspect/') && error.code === 'storage/object-not-found') {
            try {
              // Extract filename from the full path
              let extractedName = imagePath.replace(/\\/g, '/').split('/').pop();
              extractedName = extractedName.replace(/\.(jpg|png|jpeg)$/i, '');
              // Remove "watch_" prefix if present (it's part of the path, not the filename)
              if (extractedName.startsWith('watch_')) {
                extractedName = extractedName.substring(6); // Remove "watch_" (6 characters)
              }
              
              if (extractedName && extractedName.trim() !== '') {
                const fallbackPath = `${appInfo.clickimagesPath}/${extractedName}.jpg`;
                const fallbackRef = ref(storage, fallbackPath);
                const fallbackUrl = await getDownloadURL(fallbackRef);
                if (fallbackUrl && !cancelled) {
                  setImageUrl(fallbackUrl);
                  return;
                }
              }
            } catch (fallbackError) {
            }
          }
          
          if (error.code === 'storage/object-not-found') {
            setError('Image not found');
          } else {
            setError(error.message);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      cancelled = true;
    };
  }, [imagePath, appInfo]);

  if (loading) {
    return (
      <Card sx={{ width, height }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <CircularProgress size={20} />
        </CardContent>
      </Card>
    );
  }

  if (error || !imageUrl) {
    return (
      <Card sx={{ width, height }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography variant="caption" color="error" align="center">
            {error || 'No image'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card 
        sx={{ 
          width, 
          height, 
          cursor: 'pointer',
          ...(shouldIgnore ? { bgcolor: 'action.disabledBackground' } : {}),
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: 3
          }
        }}
        onClick={() => setShowFullSize(true)}
      >
        <CardMedia
          component="img"
          height={height}
          image={imageUrl}
          alt={alt}
          sx={{ objectFit: 'contain' }}
        />
      </Card>
      
      {/* Full-size image modal */}
      <Dialog
        open={showFullSize}
        onClose={() => setShowFullSize(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent 
          sx={{ 
            p: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: '70vh',
            backgroundColor: 'transparent'
          }}
          onClick={() => setShowFullSize(false)}
        >
          <img
            src={imageUrl}
            alt={alt}
            style={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain',
              cursor: 'pointer'
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

// Component for click image items in the selection dialog
const ClickImageDialogItem = ({ event, clickImageFileName, appInfo, isSelected, onToggle, videoOffset }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    
    const loadImage = async () => {
      try {
        if (cancelled) return;
        
        setLoading(true);
        setError(null);
        
        if (!appInfo || !appInfo.clickimagesPath || !clickImageFileName) {
          if (!cancelled) {
            setLoading(false);
            setError('Image path not available');
          }
          return;
        }
        
        const firebaseImagePath = `${appInfo.clickimagesPath}/${clickImageFileName}.jpg`;
        
        try {
          const imageRef = ref(storage, firebaseImagePath);
          const url = await getDownloadURL(imageRef);
          if (url && !cancelled) {
            setImageUrl(url);
          }
        } catch (error) {
          if (cancelled) return;
          
          if (error.code === 'storage/object-not-found') {
            setError('Image not found');
          } else {
            setError(error.message);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      cancelled = true;
    };
  }, [clickImageFileName, appInfo]);

  const formatTime = (ms) => {
    if (typeof ms !== 'number' || isNaN(ms)) return '';
    const sign = ms < 0 ? '-' : '';
    const absMs = Math.abs(ms);
    const hours = Math.floor(absMs / (1000 * 60 * 60));
    const minutes = Math.floor((absMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((absMs % (1000 * 60)) / 1000);
    const milliseconds = Math.floor(absMs % 1000);
    
    const pad = (n, len = 2) => n.toString().padStart(len, '0');
    
    if (hours > 0) {
      return `${sign}${hours}:${pad(minutes)}:${pad(seconds)}.${pad(milliseconds, 3)}`;
    } else {
      return `${sign}${minutes}:${pad(seconds)}.${pad(milliseconds, 3)}`;
    }
  };

  return (
    <Card 
      sx={{ 
        width: 200, 
        height: 250, 
        position: 'relative',
        border: isSelected ? 2 : 1,
        borderColor: isSelected ? 'primary.main' : 'divider',
        cursor: 'pointer'
      }}
      onClick={() => onToggle(clickImageFileName)}
    >
      <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
        <Checkbox
          checked={isSelected}
          onChange={() => onToggle(clickImageFileName)}
          onClick={(e) => e.stopPropagation()}
          size="small"
          sx={{ 
            bgcolor: 'background.paper',
            opacity: 0.9,
            '&:hover': { 
              bgcolor: 'background.default',
              opacity: 1
            }
          }}
        />
      </Box>
      {loading ? (
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <CircularProgress size={20} />
        </CardContent>
      ) : error || !imageUrl ? (
        <CardContent>
          <Typography variant="body2" color="error" align="center">
            {error || 'No image'}
          </Typography>
          {videoOffset != null && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              {formatTime(videoOffset)}
            </Typography>
          )}
        </CardContent>
      ) : (
        <>
          <CardMedia
            component="img"
            height="140"
            image={imageUrl}
            alt={`Click screenshot ${clickImageFileName}`}
            sx={{ objectFit: 'contain' }}
          />
          <CardContent>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 0.5, display: 'block' }}>
              {videoOffset != null ? formatTime(videoOffset) : clickImageFileName}
            </Typography>
            {event?.ElementControlType && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                {event.ElementControlType}
                {event.ElementControlName && `: ${event.ElementControlName}`}
              </Typography>
            )}
          </CardContent>
        </>
      )}
    </Card>
  );
};

/**
 * AutoInspectResults component displays data from autoinspect.json files
 * Props:
 *   - appInfo: Object containing file paths including autoinspect URL
 *   - runId: Inspection run ID for saving reviews to Firebase
 *   - style: Optional style object for container
 */
export default function AutoInspectResults({ appInfo, runId = null, style, viewConfig = {}, inline = false }) {
  const [open, setOpen] = useState(false);
  const [autoinspectData, setAutoinspectData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(2);
  const [saving, setSaving] = useState(false);

  const allowRequestRedo = !!viewConfig?.requestRedoViolation;
  const allowAddEditDelete = !!viewConfig?.addEditDeleteViolations;
  const allowExportReview = !!viewConfig?.exportReview;
  const allowRefreshAutoinspect = !!viewConfig?.refreshAutoinspect;
  
  // Get hints data for rule and scenario dropdowns
  const { hints, mouseClickRowsWithImages } = useData();
  const { currentUser } = useAuth();
  
  // Filter states for Verdicts by Click Image tab
  const [showApplicable, setShowApplicable] = useState(true);
  const [showViolated, setShowViolated] = useState(true);
  const [hideIgnored, setHideIgnored] = useState(false);
  
  // Edit mode and reviewed report states
  const [reviewedReport, setReviewedReport] = useState(null);
  const [editingViolations, setEditingViolations] = useState([]);
  const [editingCardIndex, setEditingCardIndex] = useState(null);
  
  // View control states
  const [hideOriginal, setHideOriginal] = useState(false);
  const [hideReviewed, setHideReviewed] = useState(false);
  
  // Filter state for Deceptor-only rules
  const [showDeceptorsOnly, setShowDeceptorsOnly] = useState(true);
  
  // Image selection dialog state
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageDialogViolationIndex, setImageDialogViolationIndex] = useState(null);
  const [selectedImagePaths, setSelectedImagePaths] = useState([]);

  // AutoInspect run status (autoinspectRun collection)
  const [autoinspectRunCount, setAutoinspectRunCount] = useState(0);
  const [lastAutoinspectRun, setLastAutoinspectRun] = useState(null);
  const [loadingAutoinspectRuns, setLoadingAutoinspectRuns] = useState(false);
  const [requestingAutoinspect, setRequestingAutoinspect] = useState(false);
  const [autoinspectRequestError, setAutoinspectRequestError] = useState(null);
  const [autoinspectRefetchError, setAutoinspectRefetchError] = useState(null);
  const autoinspectDataRef = useRef(null);
  const [autoinspectUrl, setAutoinspectUrl] = useState(appInfo?.autoinspect ?? null);
  const [redoConfirmOpen, setRedoConfirmOpen] = useState(false);
  const [redoReinspect, setRedoReinspect] = useState(true);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    setAutoinspectUrl(appInfo?.autoinspect ?? null);
  }, [appInfo?.autoinspect]);

  // Keep ref in sync for preserve-on-error logic
  useEffect(() => {
    autoinspectDataRef.current = autoinspectData;
  }, [autoinspectData]);

  // Helper function to check if a rule is a Deceptor
  const isDeceptorRule = (ruleId) => {
    if (!hints || !Array.isArray(hints) || !ruleId) return false;
    const hint = hints.find(h => h.id && h.id.toLowerCase() === ruleId.toLowerCase());
    return hint?.isDeceptor === true;
  };

  // Helper function to get all unique rule IDs from hints
  const getRuleIds = () => {
    if (!hints || !Array.isArray(hints)) return [];
    let filteredHints = hints;
    if (showDeceptorsOnly) {
      filteredHints = hints.filter(hint => hint.isDeceptor === true);
    }
    return filteredHints
      .map(hint => hint.id)
      .filter(Boolean)
      .sort();
  };

  // Helper function to get valid scenarios for a given rule ID
  const getScenariosForRule = (ruleId) => {
    if (!hints || !Array.isArray(hints) || !ruleId) return [];
    // Match case-insensitively to handle both "acr-044" and "ACR-044" formats
    const hint = hints.find(h => h.id && h.id.toLowerCase() === ruleId.toLowerCase());
    if (!hint || !hint.scenarios || !Array.isArray(hint.scenarios)) return [];
    return [...hint.scenarios].sort();
  };

  // Reusable fetch for autoinspect.json; preserves existing data on error and sets refetch-error message
  // urlOverride: when provided (e.g. from refetchAutoInspect), use it for the request instead of autoinspectUrl
  const fetchAutoinspectData = useCallback(async (urlOverride = null) => {
    const url = urlOverride ?? autoinspectUrl;
    setLoading(true);
    setAutoinspectRefetchError(null);
    let fetchSucceeded = false;
    try {
      let data = null;
      if (url) {
        const response = await fetch(url);
        if (response.ok) {
          data = await response.json();
          fetchSucceeded = true;
        }
      } else {
        // No URL provided, this is not an error
        fetchSucceeded = true;
      }
      if (fetchSucceeded) {
        setAutoinspectData(data);
      }
    } catch (error) {
      // Fetch failed
      if (autoinspectDataRef.current != null) {
        setAutoinspectRefetchError('Could not refresh; data may be outdated.');
      } else {
        setAutoinspectData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [autoinspectUrl]);

  // Fetch autoinspect.json when dialog opens (or on mount when inline) or URL changes
  const isActive = inline || open;
  useEffect(() => {
    if (isActive) {
      fetchAutoinspectData();
    }
  }, [isActive, fetchAutoinspectData]);

  // Load existing reviews from Firebase when dialog opens (or on mount when inline)
  useEffect(() => {
    const loadExistingReviews = async () => {
      if (!isActive || !runId) return;
      
      try {
        // Query the violations subcollection for this run
        const violationsRef = collection(db, 'inspectionReview', runId, 'violations');
        const querySnapshot = await getDocs(violationsRef);
        
        if (!querySnapshot.empty) {
          // Build reviewed report from individual documents
          const reviewedViolations = autoinspectData?.summarizedInfo?.results ? [...autoinspectData.summarizedInfo.results] : [];
          let latestReviewTime = null;
          
          querySnapshot.forEach((docSnap) => {
            const reviewData = docSnap.data();
            // Find matching violation by RuleID and Scenario
            const index = reviewedViolations.findIndex(
              v => v.RuleID === reviewData.RuleID && v.Scenario === reviewData.Scenario
            );
            
            if (index !== -1) {
              reviewedViolations[index] = {
                RuleID: reviewData.RuleID,
                Scenario: reviewData.Scenario,
                ViolationSummary: reviewData.ViolationSummary,
                EvidenceFileNames: reviewData.EvidenceFileNames || [],
                ignore: reviewData.ignore === true,
                lastModified: reviewData.lastModified
              };
              
              // Track latest review time
              if (!latestReviewTime || new Date(reviewData.lastModified) > new Date(latestReviewTime)) {
                latestReviewTime = reviewData.lastModified;
              }
            }
          });
          
          const reviewed = {
            baseReportTime: autoinspectData.summarizeTime,
            reviewTime: latestReviewTime || new Date().toISOString(),
            results: reviewedViolations
          };
          setReviewedReport(reviewed);
        }
      } catch (error) {
        console.error('Error loading existing reviews:', error);
      }
    };
    
    loadExistingReviews();
  }, [isActive, runId, autoinspectData]);

  // Real-time autoinspectRun count and last run (when active and runId set)
  useEffect(() => {
    if (!isActive || !runId) {
      setAutoinspectRunCount(0);
      setLastAutoinspectRun(null);
      setLoadingAutoinspectRuns(false);
      return;
    }
    setLoadingAutoinspectRuns(true);
    const runsRef = collection(db, 'autoinspectRun');
    const countQuery = query(runsRef, where('InspectionRunId', '==', runId));
    const lastQuery = query(
      runsRef,
      where('InspectionRunId', '==', runId),
      orderBy('CreatedAt', 'desc'),
      limit(1)
    );
    const unsubCount = onSnapshot(
      countQuery,
      (snapshot) => {
        setAutoinspectRunCount(snapshot.size);
        setLoadingAutoinspectRuns(false);
      },
      (err) => {
        console.error('Error listening to autoinspect run count:', err);
        setAutoinspectRunCount(0);
        setLoadingAutoinspectRuns(false);
      }
    );
    const unsubLast = onSnapshot(
      lastQuery,
      (snapshot) => {
        if (!snapshot.empty) {
          const data = snapshot.docs[0].data();
          setLastAutoinspectRun({
            status: data.Status ?? data.status ?? null,
            createdAt: data.CreatedAt ?? data.createdAt ?? null
          });
        } else {
          setLastAutoinspectRun(null);
        }
      },
      (err) => {
        console.error('Error listening to last autoinspect run:', err);
        setLastAutoinspectRun(null);
      }
    );
    return () => {
      unsubCount();
      unsubLast();
    };
  }, [isActive, runId]);

  const handleRequestAutoinspect = async (inspectionType = 'inspect') => {
    if (!runId) {
      setAutoinspectRequestError('Inspection run ID is missing.');
      return;
    }
    const status = lastAutoinspectRun?.status?.toLowerCase?.();
    if (autoinspectData && status && status !== 'failed' && status !== 'completed') {
      setAutoinspectRequestError('Request is available when the last run is completed or failed.');
      return;
    }
    console.log("Here is the inspection type", inspectionType);
    setAutoinspectRequestError(null);
    setRequestingAutoinspect(true);
    try {
      const url = `${INSPECTORCLICK_API_BASE_URL}/inspectionRun/${runId}/autoinspect?inspectionType=${inspectionType}`;
      const headers = {};
      if (currentUser) {
        const idToken = await currentUser.getIdToken();
        headers.Authorization = `Bearer ${idToken}`;
      }
      const res = await fetch(url, { method: 'POST', headers });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Request failed: ${res.status}`);
      }
    } catch (err) {
      setAutoinspectRequestError(err.message || 'Failed to request autoinspect.');
    } finally {
      setRequestingAutoinspect(false);
    }
  };

  const handleOpenRedoConfirm = () => {
    setRedoReinspect(true);
    setRedoConfirmOpen(true);
  };

  const handleConfirmRedo = () => {
    setRedoConfirmOpen(false);
    handleRequestAutoinspect(redoReinspect ? 'reinspect' : 'inspect');
  };

  const handleRefreshAutoinspect = async () => {
    setAutoinspectRefetchError(null);
    if (appInfo?.refetchAutoInspect) {
      const newUrl = await appInfo.refetchAutoInspect();
      setAutoinspectUrl(newUrl);
      fetchAutoinspectData(newUrl);
    } else {
      fetchAutoinspectData();
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };



  // Start editing a specific card
  const handleStartEditCard = (index) => {
    // Initialize editingViolations if not already done
    if (editingViolations.length === 0) {
      const dataToEdit = reviewedReport ? reviewedReport.results : autoinspectData?.summarizedInfo?.results;
      if (dataToEdit) {
        setEditingViolations(JSON.parse(JSON.stringify(dataToEdit)));
      }
    }
    setEditingCardIndex(index);
  };

  // Cancel editing a specific card
  const handleCancelEditCard = () => {
    // Restore from reviewed report or original
    const dataToEdit = reviewedReport ? reviewedReport.results : autoinspectData?.summarizedInfo?.results;
    if (dataToEdit) {
      setEditingViolations(JSON.parse(JSON.stringify(dataToEdit)));
    }
    setEditingCardIndex(null);
  };

  // Save changes for a specific card
  const handleSaveEditCard = async () => {
    if (!runId) {
      alert('Unable to save: Inspection run ID is missing.');
      return;
    }
    
    setSaving(true);
    try {
      const currentViolation = editingViolations[editingCardIndex];
      // Find original violation by matching RuleID and Scenario, or by index if it exists
      const originalViolation = autoinspectData?.summarizedInfo?.results?.find(
        v => v.RuleID === currentViolation.RuleID && v.Scenario === currentViolation.Scenario
      ) || (autoinspectData?.summarizedInfo?.results?.[editingCardIndex]);
      const timestamp = new Date().toISOString();
      
      // Create document ID from RuleID and Scenario
      const violationDocId = `${currentViolation.RuleID}_${currentViolation.Scenario}`.replace(/[^a-zA-Z0-9_-]/g, '_');
      
      // First, ensure the parent review document exists with metadata
      const reviewDocRef = doc(db, 'inspectionReview', runId);
      await setDoc(reviewDocRef, {
        InspectionRunId: runId,
        baseReportTime: autoinspectData.summarizeTime,
        lastModified: timestamp,
        createdAt: timestamp
      }, { merge: true });
      
      // Validate required fields for new violations
      if (!currentViolation.RuleID || !currentViolation.Scenario || !currentViolation.ViolationSummary) {
        alert('Please fill in all required fields: Rule ID, Scenario, and Violation Summary.');
        setSaving(false);
        return;
      }

      // Prepare violation document data
      const violationDoc = {
        RuleID: currentViolation.RuleID,
        Scenario: currentViolation.Scenario,
        ViolationSummary: currentViolation.ViolationSummary,
        EvidenceFileNames: currentViolation.EvidenceFileNames || [],
        ignore: currentViolation.ignore === true,
        lastModified: timestamp
      };

      // Add original violation data if this is an edit of an existing violation
      if (originalViolation) {
        violationDoc.originalViolationSummary = originalViolation.ViolationSummary;
        violationDoc.originalEvidenceFileNames = originalViolation.EvidenceFileNames || [];
      }
      
      // Save to violations subcollection
      const violationDocRef = doc(db, 'inspectionReview', runId, 'violations', violationDocId);
      await setDoc(violationDocRef, violationDoc);
      
      // Update local state
      const updatedViolations = [...editingViolations];
      updatedViolations[editingCardIndex] = {
        ...updatedViolations[editingCardIndex],
        lastModified: timestamp
      };
      
      const reviewed = {
        baseReportTime: autoinspectData.summarizeTime,
        reviewTime: timestamp,
        results: updatedViolations
      };
      setReviewedReport(reviewed);
      setEditingViolations(updatedViolations);
      setEditingCardIndex(null);
    } catch (error) {
      console.error('Error saving review:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Update violation summary text
  const handleUpdateSummary = (index, newSummary) => {
    const updated = [...editingViolations];
    updated[index] = { ...updated[index], ViolationSummary: newSummary };
    setEditingViolations(updated);
  };

  // Update violation RuleID
  const handleUpdateRuleID = (index, newRuleID) => {
    const updated = [...editingViolations];
    const currentViolation = updated[index];
    const validScenarios = getScenariosForRule(newRuleID);
    
    // If the current scenario is not valid for the new rule, clear it
    const scenarioToKeep = validScenarios.includes(currentViolation?.Scenario) 
      ? currentViolation.Scenario 
      : '';
    
    updated[index] = { 
      ...currentViolation, 
      RuleID: newRuleID,
      Scenario: scenarioToKeep
    };
    setEditingViolations(updated);
  };

  // Update violation Scenario
  const handleUpdateScenario = (index, newScenario) => {
    const updated = [...editingViolations];
    updated[index] = { ...updated[index], Scenario: newScenario };
    setEditingViolations(updated);
  };

  // Remove evidence image
  const handleRemoveEvidence = (violationIndex, imageIndex) => {
    const updated = [...editingViolations];
    const evidenceFiles = [...(updated[violationIndex].EvidenceFileNames || [])];
    evidenceFiles.splice(imageIndex, 1);
    updated[violationIndex] = { ...updated[violationIndex], EvidenceFileNames: evidenceFiles };
    setEditingViolations(updated);
  };

  // Helper function to convert EvidenceFileNames path to clickImageFileName
  const extractClickImageFileName = (evidencePath) => {
    if (!evidencePath) return null;
    // Handle formats: "clickimages/xxx.jpg", "Inspect/...", or just "xxx"
    let fileName = evidencePath;
    
    // If it's a path, extract the filename
    if (fileName.includes('/')) {
      fileName = fileName.replace(/\\/g, '/').split('/').pop();
    }
    
    // Remove extension
    fileName = fileName.replace(/\.(jpg|png|jpeg)$/i, '');
    
    // Remove "watch_" prefix if present
    if (fileName.startsWith('watch_')) {
      fileName = fileName.substring(6);
    }
    
    return fileName || null;
  };

  // Helper function to convert clickImageFileName to storage path format
  const getImageStoragePath = (clickImageFileName) => {
    if (!clickImageFileName) return null;
    return `clickimages/${clickImageFileName}.jpg`;
  };

  // Update evidence images from dialog selection
  const handleUpdateEvidenceImages = (violationIndex, selectedPaths) => {
    const updated = [...editingViolations];
    updated[violationIndex] = { 
      ...updated[violationIndex], 
      EvidenceFileNames: selectedPaths.filter(Boolean) // Filter out any null/undefined values
    };
    setEditingViolations(updated);
  };

  // Open image selection dialog
  const handleOpenImageDialog = (violationIndex) => {
    // Get current violation data - use editingViolations if available, otherwise fall back to reviewedReport or autoinspectData
    const currentViolations = editingViolations.length > 0 
      ? editingViolations 
      : (reviewedReport?.results || autoinspectData?.summarizedInfo?.results || []);
    
    const violation = currentViolations[violationIndex];
    if (!violation) {
      console.error('Violation not found at index:', violationIndex);
      return;
    }
    
    const currentEvidencePaths = violation.EvidenceFileNames || [];
    
    // Initialize selected paths: convert current evidence to storage path format
    const initialSelectedPaths = currentEvidencePaths.length > 0
      ? currentEvidencePaths.map(path => {
          const fileName = extractClickImageFileName(path);
          return fileName ? getImageStoragePath(fileName) : path;
        }).filter(Boolean)
      : [];
    
    setImageDialogViolationIndex(violationIndex);
    setSelectedImagePaths(initialSelectedPaths);
    setImageDialogOpen(true);
  };

  // Close image selection dialog
  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
    setImageDialogViolationIndex(null);
    setSelectedImagePaths([]);
  };

  // Apply selected images to violation
  const handleApplyImageSelection = () => {
    if (imageDialogViolationIndex !== null) {
      handleUpdateEvidenceImages(imageDialogViolationIndex, selectedImagePaths);
    }
    handleCloseImageDialog();
  };

  // Toggle image selection in dialog
  const handleToggleImageSelection = (clickImageFileName) => {
    const imagePath = getImageStoragePath(clickImageFileName);
    setSelectedImagePaths(prev => {
      if (prev.includes(imagePath)) {
        return prev.filter(path => path !== imagePath);
      } else {
        return [...prev, imagePath];
      }
    });
  };

  // Toggle ignore flag for a violation
  const handleToggleIgnore = (index) => {
    const updated = [...editingViolations];
    if (updated.length === 0) {
      const dataToEdit = reviewedReport ? reviewedReport.results : autoinspectData?.summarizedInfo?.results;
      if (dataToEdit) {
        setEditingViolations(JSON.parse(JSON.stringify(dataToEdit)));
        const newUpdated = [...JSON.parse(JSON.stringify(dataToEdit))];
        newUpdated[index] = { ...newUpdated[index], ignore: !(newUpdated[index]?.ignore === true) };
        setEditingViolations(newUpdated);
      }
    } else {
      updated[index] = { ...updated[index], ignore: !(updated[index]?.ignore === true) };
      setEditingViolations(updated);
    }
  };

  // Prompt to delete a violation — opens the confirmation dialog
  const handleDeleteViolation = (index) => {
    setDeleteError(null);

    if (!runId) {
      setDeleteError('Unable to delete violation: Inspection run ID is missing.');
      setDeleteConfirmIndex(index);
      return;
    }

    const violationToDelete = editingViolations[index] || reviewedReport?.results[index];
    if (!violationToDelete) return;

    const isNewViolation = !autoinspectData?.summarizedInfo?.results?.find(
      v => v.RuleID === violationToDelete.RuleID && v.Scenario === violationToDelete.Scenario
    );

    if (!isNewViolation) {
      setDeleteError('Cannot delete: This violation exists in the original autoinspect report. You can ignore it instead.');
      setDeleteConfirmIndex(index);
      return;
    }

    setDeleteConfirmIndex(index);
  };

  const handleConfirmDelete = async () => {
    const index = deleteConfirmIndex;
    setDeleteConfirmIndex(null);
    if (index == null) return;

    const violationToDelete = editingViolations[index] || reviewedReport?.results[index];
    if (!violationToDelete) return;

    setSaving(true);
    try {
      // Delete from Firebase if it exists there
      if (violationToDelete.RuleID && violationToDelete.Scenario) {
        const violationDocId = `${violationToDelete.RuleID}_${violationToDelete.Scenario}`.replace(/[^a-zA-Z0-9_-]/g, '_');
        const violationDocRef = doc(db, 'inspectionReview', runId, 'violations', violationDocId);
        try {
          await deleteDoc(violationDocRef);
        } catch (error) {
          // Document might not exist in Firebase yet, which is fine
          console.log('Violation document not found in Firebase (may not have been saved yet):', error);
        }
      }

      // Remove from local state
      const updatedViolations = [...editingViolations];
      updatedViolations.splice(index, 1);
      setEditingViolations(updatedViolations);

      // Update reviewed report
      if (reviewedReport) {
        const updatedReviewed = {
          ...reviewedReport,
          results: updatedViolations
        };
        setReviewedReport(updatedReviewed);
      }

      // Clear editing state if we deleted the card being edited
      if (editingCardIndex === index) {
        setEditingCardIndex(null);
      } else if (editingCardIndex > index) {
        // Adjust editing index if we deleted a card before the one being edited
        setEditingCardIndex(editingCardIndex - 1);
      }
    } catch (error) {
      console.error('Error deleting violation:', error);
      setDeleteError('Failed to delete violation: ' + error.message);
      setDeleteConfirmIndex(-1);
    } finally {
      setSaving(false);
    }
  };

  // Add a new violation
  const handleAddViolation = () => {
    if (!runId) {
      alert('Unable to add violation: Inspection run ID is missing.');
      return;
    }

    // Initialize editing violations if not already done
    if (editingViolations.length === 0) {
      const dataToEdit = reviewedReport ? reviewedReport.results : (autoinspectData?.summarizedInfo?.results || []);
      setEditingViolations(JSON.parse(JSON.stringify(dataToEdit)));
    }

    // Create a new empty violation
    const newViolation = {
      RuleID: '',
      Scenario: '',
      ViolationSummary: '',
      EvidenceFileNames: [],
      ignore: false
    };

    // Add new violation at the top of the list
    const updated = [newViolation, ...editingViolations];
    setEditingViolations(updated);
    
    // Set the new violation as the one being edited (index 0 since it's at the top)
    setEditingCardIndex(0);
    
    // Update reviewed report if it exists, otherwise create it
    if (reviewedReport) {
      const updatedReviewed = {
        ...reviewedReport,
        results: updated
      };
      setReviewedReport(updatedReviewed);
    } else {
      // Create a new reviewed report
      const newReviewed = {
        baseReportTime: autoinspectData?.summarizeTime || null,
        reviewTime: new Date().toISOString(),
        results: updated
      };
      setReviewedReport(newReviewed);
    }
  };



  // Export reviewed report as JSON
  const handleExportReview = () => {
    if (!reviewedReport) return;
    
    const dataStr = JSON.stringify(reviewedReport, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reviewed-report-${new Date().toISOString()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Filter function to return filtered results array for each verdict
  const getFilteredResults = (verdict) => {
    if (!verdict.results || verdict.results.length === 0) return [];
    
    // If no filters are active, return all results
    if (!showApplicable && !showViolated) return verdict.results;
    
    return verdict.results.filter(result => {
      // If "Applicable only" is checked, only show results where IsApplicable is true
      if (showApplicable && !showViolated) {
        return result.IsApplicable === true;
      }
      
      // If "Violations only" is checked, only show results where AcrViolated is true
      if (showViolated && !showApplicable) {
        return result.AcrViolated === true;
      }
      
      // If both filters are active, show results that are both applicable AND violated
      if (showApplicable && showViolated) {
        return result.IsApplicable === true && result.AcrViolated === true;
      }
      
      return true;
    });
  };

  const violationsContent = (
    <>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading autoinspect data...</Typography>
        </Box>
      ) : (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              
              {/* Status strip */}
              <Box
                sx={{
                  flexShrink: 0,
                  mb: 1.5,
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  bgcolor: 'action.hover',
                }}
              >
                {loadingAutoinspectRuns ? (
                  <Typography variant="body2" color="text.secondary">Loading autoinspect run info...</Typography>
                ) : (
                  <>
                    {allowRequestRedo && (
                      <>
                        {!autoinspectData ? (
                          <>
                            <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                              {!autoinspectUrl && !loading
                                ? 'autoinspect.json not in storage yet. Click refresh after the run completes.'
                                : 'No autoinspect.json file available.'}
                              {lastAutoinspectRun && ` Last run: ${lastAutoinspectRun.status ?? '—'}.`}
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={requestingAutoinspect ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                              disabled={!runId || requestingAutoinspect || (lastAutoinspectRun?.status != null && !['failed', 'completed'].includes(String(lastAutoinspectRun.status).toLowerCase()))}
                              onClick={() => handleRequestAutoinspect('inspect')}
                              title={!runId ? 'Run ID missing' : (lastAutoinspectRun?.status != null && !['failed', 'completed'].includes(String(lastAutoinspectRun.status).toLowerCase())) ? 'Request available when last run is completed or failed' : 'Request autoinspect run'}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              Request
                            </Button>
                          </>
                        ) : (
                          <>
                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                              Autoinspect runs: <strong>{autoinspectRunCount}</strong>
                              {lastAutoinspectRun && ` · Last run: ${lastAutoinspectRun.status ?? '—'}`}
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={requestingAutoinspect ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                              disabled={!runId || requestingAutoinspect || (lastAutoinspectRun?.status != null && !['failed', 'completed'].includes(String(lastAutoinspectRun.status).toLowerCase()))}
                              onClick={handleOpenRedoConfirm}
                              title={lastAutoinspectRun?.status != null && !['failed', 'completed'].includes(String(lastAutoinspectRun.status).toLowerCase()) ? 'Rerun available when last run is completed or failed' : 'Run autoinspect again'}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              Rerun
                            </Button>
                          </>
                        )}
                      </>
                    )}
                    {allowRefreshAutoinspect && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleRefreshAutoinspect}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Refresh
                      </Button>
                    )}
                    {autoinspectRequestError && (
                      <Typography variant="caption" color="error" sx={{ width: '100%', textAlign: 'right' }}>
                        {autoinspectRequestError}
                      </Typography>
                    )}
                    {autoinspectRefetchError && (
                      <Typography variant="caption" color="warning.main" sx={{ width: '100%', textAlign: 'right' }}>
                        {autoinspectRefetchError}
                      </Typography>
                    )}
                  </>
                )}
              </Box>

              {/* Tabs for the main content - Fixed at top */}
              <Box sx={{ flexShrink: 0 }}>
                <Tabs 
                  value={activeTab} 
                  onChange={handleTabChange} 
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                >
                  <Tab 
                    label={`Verdicts by Click Image (${autoinspectData ? Object.keys(autoinspectData.verdictsByClickImage || {}).length : 0})`}
                    disabled={!autoinspectData}
                  />
                  <Tab 
                    label={`Failed ACRs (${autoinspectData ? Object.keys(autoinspectData.failedACRs || {}).length : 0})`}
                    disabled={!autoinspectData}
                  />
                  <Tab 
                    label={`Violations (${autoinspectData?.summarizedInfo?.results?.length || reviewedReport?.results?.length || 0})`} 
                  />
                </Tabs>
                
                {/* Filter controls for first tab - Fixed at top */}
                {activeTab === 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showApplicable}
                            onChange={(e) => setShowApplicable(e.target.checked)}
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ToggleOnIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="caption">Applicable only</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showViolated}
                            onChange={(e) => setShowViolated(e.target.checked)}
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                            <Typography variant="caption">Violations only</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={hideIgnored}
                            onChange={(e) => setHideIgnored(e.target.checked)}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="caption">Hide ignored</Typography>
                        }
                      />
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Tab Content - Scrollable */}
              <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                  {/* Verdicts by Click Image Tab */}
                  {activeTab === 0 && (
                    <Box>
                      {autoinspectData.verdictsByClickImage && Object.keys(autoinspectData.verdictsByClickImage).length > 0 ? (
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Image</strong></TableCell>
                                <TableCell><strong>Results</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(autoinspectData.verdictsByClickImage)
                                .filter(([imageFile, verdict]) => {
                                  // Filter out ignored images if hideIgnored is true
                                  if (hideIgnored && verdict.shouldIgnoreClickImage === true) {
                                    return false;
                                  }
                                  return true;
                                })
                                .map(([imageFile, verdict]) => {
                                  const filteredResults = getFilteredResults(verdict);
                                  return (
                                    <TableRow 
                                      key={imageFile}
                                      sx={
                                        verdict.shouldIgnoreClickImage === true
                                          ? (theme) => ({ backgroundColor: theme.palette.action.disabledBackground })
                                          : {}
                                      }
                                    >
                                      <TableCell sx={{ verticalAlign: 'top', padding: 1, width: 250, maxWidth: 250, minWidth: 250 }}>
                                        <AutoInspectImage 
                                          imagePath={imageFile} 
                                          appInfo={appInfo}
                                          alt={`AutoInspect ${imageFile.split('/').pop()}`}
                                          shouldIgnore={verdict.shouldIgnoreClickImage === true}
                                        />
                                        <Typography variant="caption" sx={{ 
                                          display: 'block', 
                                          mt: 1, 
                                          fontSize: '0.75rem',
                                          wordBreak: 'break-all',
                                          fontWeight: 'bold',
                                          textAlign: 'left'
                                        }}>
                                          {imageFile.split('/').pop()}
                                        </Typography>
                                        <Typography variant="caption" sx={{ 
                                          display: 'block', 
                                          mt: 0.5, 
                                          fontSize: '0.8rem',
                                          color: 'text.secondary',
                                          textAlign: 'left'
                                        }}>
                                          {verdict.imageDescription || 'No description available'}
                                        </Typography>
                                        <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'center', gap: 0.5 }}>
                                          {verdict.inspectionPhase && (
                                            <Chip
                                                label={verdict.inspectionPhase}
                                              size="small"
                                              color="primary"
                                            />
                                          )}
                                          {verdict.applicableScenarios && verdict.applicableScenarios.map((scenario, idx) => (
                                            <Chip key={idx} label={scenario} color="secondary" variant="outlined" size="small" />
                                          ))}
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: 'top' }}>
                                        {filteredResults.length > 0 ? (
                                          <Box>
                                            {filteredResults.map((result, idx) => (
                                              <Box key={idx} sx={{ mb: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', minWidth: '60px' }}>
                                                  {result.Id || `Item ${idx + 1}`}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: '60px' }}>
                                                  {result.IsApplicable !== undefined && (
                                                    result.IsApplicable ? 
                                                      <ToggleOnIcon sx={{ fontSize: 18, color: 'primary.main' }} title="Applicable" /> : 
                                                      <ToggleOffIcon sx={{ fontSize: 18, color: 'text.disabled' }} title="Not Applicable" />
                                                  )}
                                                  {result.AcrViolated !== undefined && (
                                                    result.AcrViolated ? 
                                                      <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} title="ACR Violated" /> : 
                                                      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} title="ACR Not Violated" />
                                                  )}
                                                </Box>
                                                <Typography variant="body2" sx={{ fontSize: '0.875rem', flex: 1 }}>
                                                  {result.Reason || 'No reason provided'}
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            {(showApplicable || showViolated) ? 'No results match the selected filters' : 'No results available'}
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No verdicts by click image data available.
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Failed ACRs Tab */}
                  {activeTab === 1 && (
                    <Box>
                      {autoinspectData.failedACRs && Object.keys(autoinspectData.failedACRs).length > 0 ? (
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Image</strong></TableCell>
                                <TableCell><strong>Verdict</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(autoinspectData.failedACRs).map(([acrId, failures]) => 
                                Array.isArray(failures) ? (
                                  <React.Fragment key={acrId}>
                                    {/* ACR Header Row */}
                                    <TableRow
                                      sx={(theme) => ({
                                        // Subtle header background (no zebra striping), works in both light and dark mode
                                        backgroundColor: alpha(
                                          theme.palette.text.primary,
                                          theme.palette.mode === 'dark' ? 0.10 : 0.04
                                        ),
                                        '& td': { borderBottom: 'none' }
                                      })}
                                    >
                                      <TableCell colSpan={2} sx={{ py: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                            {acrId}
                                          </Typography>
                                          <Chip 
                                            label={`${failures.length} violation${failures.length > 1 ? 's' : ''}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ fontWeight: 'medium' }}
                                          />
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                    {/* Violation Detail Rows */}
                                    {failures.map((failure, idx) => {
                                      return (
                                      <TableRow
                                        key={`${acrId}-${idx}`}
                                        sx={(theme) => ({
                                          '& td': {
                                            borderTop: 'none',
                                            // No zebra striping (works in both light and dark mode)
                                            backgroundColor: 'transparent'
                                          }
                                        })}
                                      >
                                        <TableCell sx={{ verticalAlign: 'top', padding: 1, width: 250, maxWidth: 250, minWidth: 250 }}>
                                          {failure.clickImageFile && (
                                            <>
                                              <AutoInspectImage 
                                                imagePath={failure.clickImageFile} 
                                                appInfo={appInfo}
                                                alt={`Failed ACR ${acrId} evidence ${idx + 1}`}
                                              />
                                              <Typography variant="caption" sx={{ 
                                                display: 'block', 
                                                mt: 1, 
                                                fontSize: '0.75rem',
                                                wordBreak: 'break-all',
                                                fontWeight: 'bold',
                                                textAlign: 'left'
                                              }}>
                                                {failure.clickImageFile.split('/').pop()}
                                              </Typography>
                                            </>
                                          )}
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', maxWidth: 500 }}>
                                          {failure.verdict?.Id && (
                                            <Typography variant="caption" sx={{ 
                                              fontSize: '0.7rem', 
                                              color: 'text.secondary', 
                                              fontWeight: 'medium',
                                              display: 'block',
                                              mb: 0.5
                                            }}>
                                              {failure.verdict.Id}
                                            </Typography>
                                          )}
                                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                            {failure.verdict?.AcrViolated ? 
                                              <CancelIcon sx={{ fontSize: 18, color: 'error.main', mt: 0.1 }} title="ACR Violated" /> : 
                                              <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', mt: 0.1 }} title="ACR Not Violated" />
                                            }
                                            <Typography variant="body2" sx={{ fontSize: '0.875rem', flex: 1 }}>
                                              {failure.verdict?.Reason || 'No reason provided'}
                                            </Typography>
                                          </Box>
                                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'center', gap: 0.5 }}>
                                            {failure.inspectionPhase && (
                                              <Chip
                                                label={failure.inspectionPhase}
                                                size="small"
                                                color="primary"
                                              />
                                            )}
                                            {failure.scenarios && failure.scenarios.map((scenario, i) => (
                                              <Chip key={i} label={scenario} size="small" variant="outlined" color="secondary" />
                                            ))}
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                      );
                                    })}
                                  </React.Fragment>
                                ) : (
                                  <TableRow key={acrId}>
                                    <TableCell colSpan={2}>
                                      <pre style={{ 
                                        margin: 0, 
                                        fontSize: '0.7rem', 
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'monospace',
                                        backgroundColor: 'transparent'
                                      }}>
                                        {JSON.stringify(failures, null, 2)}
                                      </pre>
                                    </TableCell>
                                  </TableRow>
                                )
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No failed ACRs data available.
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Violations Tab */}
                  {activeTab === 2 && (
                    <Box>
                      {/* Info message if no autoinspectData */}
                      {!autoinspectData && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          No autoinspect.json; you can request a run above or add violations manually.
                        </Alert>
                      )}
                      
                      {/* Warning if no runId */}
                      {!runId && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          No inspection run ID available. Violations cannot be saved.
                        </Alert>
                      )}
                      
                      {/* Review controls */}
                      {(autoinspectData?.summarizedInfo?.results && autoinspectData.summarizedInfo.results.length > 0) || reviewedReport ? (
                        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {reviewedReport && (
                              <ToggleButtonGroup
                                size="small"
                                value={[!hideOriginal && 'original', !hideReviewed && 'reviewed'].filter(Boolean)}
                                onChange={(event, newValues) => {
                                  // Ensure at least one is always selected
                                  if (newValues.length > 0) {
                                    setHideOriginal(!newValues.includes('original'));
                                    setHideReviewed(!newValues.includes('reviewed'));
                                  }
                                }}
                                sx={{ mr: 1 }}
                              >
                                <ToggleButton value="original" sx={{ px: 1.5, py: 0.5, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  <AssessmentIcon sx={{ fontSize: 16 }} />
                                  <Typography variant="caption">Original</Typography>
                                </ToggleButton>
                                <ToggleButton value="reviewed" sx={{ px: 1.5, py: 0.5, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                                  <Typography variant="caption">Reviewed</Typography>
                                </ToggleButton>
                              </ToggleButtonGroup>
                            )}
                            
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip 
                              label={`Reviewed on ${new Date(reviewedReport?.reviewTime || new Date()).toLocaleString()}`}
                              color="success"
                              size="small"
                              sx={{ visibility: reviewedReport ? 'visible' : 'hidden' }}
                            />
                            
                            {reviewedReport ? (
                              <>
                                {allowAddEditDelete && (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddViolation}
                                    size="small"
                                    title={!runId ? 'Warning: Run ID missing - changes cannot be saved' : 'Add a new violation'}
                                  >
                                    Add Violation
                                  </Button>
                                )}
                                {allowExportReview && (
                                  <Button
                                    variant="outlined"
                                    onClick={handleExportReview}
                                    size="small"
                                  >
                                    Export Review
                                  </Button>
                                )}
                              </>
                            ) : (
                              allowAddEditDelete && (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  startIcon={<EditIcon />}
                                  onClick={() => {
                                    if (!runId) {
                                      alert('Warning: Inspection run ID is missing. Reviews cannot be saved.');
                                    }
                                    // Initialize a reviewed report from the original data
                                    const initialReview = {
                                      baseReportTime: autoinspectData?.summarizeTime || null,
                                      reviewTime: new Date().toISOString(),
                                      results: autoinspectData?.summarizedInfo?.results ? JSON.parse(JSON.stringify(autoinspectData.summarizedInfo.results)) : []
                                    };
                                    setReviewedReport(initialReview);
                                    setEditingViolations(initialReview.results);
                                  }}
                                  size="small"
                                  title={!runId ? 'Warning: Run ID missing - changes cannot be saved' : ''}
                                >
                                  Create Review
                                </Button>
                              )
                            )}
                          </Box>
                        </Box>
                      ) : (
                        // Show Add Violation button even when no autoinspectData exists (hidden when not allowed)
                        runId && allowAddEditDelete && (
                          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={handleAddViolation}
                              size="small"
                            >
                              Add Violation
                            </Button>
                          </Box>
                        )
                      )}
                      {(autoinspectData?.summarizedInfo?.results && autoinspectData.summarizedInfo.results.length > 0) || reviewedReport ? (
                        <Box>
                          {/* Show side-by-side comparison when reviewed report exists */}
                          {reviewedReport ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {/* Header Row */}
                              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                {!hideOriginal && (
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                      Original Auto Inspect Report
                                    </Typography>
                                  </Box>
                                )}
                                {!hideReviewed && (
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                      Reviewed Report
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                              
                              {/* Violation Cards - Side by Side with Inline Editing */}
                              {reviewedReport.results.map((reviewedViolation, index) => {
                                const isEditing = editingCardIndex === index;
                                const displayViolation = isEditing && editingViolations[index] ? editingViolations[index] : reviewedViolation;
                                
                                // Find corresponding original violation if it exists
                                const originalViolation = autoinspectData?.summarizedInfo?.results?.find(
                                  v => v.RuleID === reviewedViolation.RuleID && v.Scenario === reviewedViolation.Scenario
                                );
                                
                                // Check if the reviewed violation is different from original (if original exists)
                                const summaryChanged = originalViolation && originalViolation.ViolationSummary !== displayViolation?.ViolationSummary;
                                const evidenceChanged = originalViolation && JSON.stringify(originalViolation.EvidenceFileNames || []) !== JSON.stringify(displayViolation?.EvidenceFileNames || []);
                                const hasChanges = summaryChanged || evidenceChanged;
                                const isNewViolation = !originalViolation; // This is a new violation not in autoinspectData
                                
                                return (
                                  <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
                                    {/* Original Violation - Left */}
                                    {!hideOriginal && (
                                      <Paper sx={{ flex: 1, p: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
                                      {originalViolation ? (
                                        <>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                              {originalViolation.RuleID || 'Unknown Rule'}
                                            </Typography>
                                            <Chip 
                                              label={originalViolation.Scenario || 'Unknown Scenario'} 
                                              size="small" 
                                              variant="outlined"
                                              color="secondary"
                                            />
                                          </Box>
                                          <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem', lineHeight: 1.5 }}>
                                            {originalViolation.ViolationSummary || 'No violation summary available.'}
                                          </Typography>
                                          {originalViolation.EvidenceFileNames && originalViolation.EvidenceFileNames.length > 0 && (
                                            <Box>
                                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {originalViolation.EvidenceFileNames.map((fileName, idx) => (
                                                  <Box key={idx} sx={{ width: 120, height: 90, flexShrink: 0 }}>
                                                    <AutoInspectImage 
                                                      imagePath={fileName} 
                                                      appInfo={appInfo}
                                                      alt={`Evidence ${idx + 1} for ${originalViolation.RuleID}`}
                                                      width={120}
                                                      height={90}
                                                    />
                                                  </Box>
                                                ))}
                                              </Box>
                                            </Box>
                                          )}
                                        </>
                                      ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 100 }}>
                                          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                            New violation (not in original report)
                                          </Typography>
                                        </Box>
                                      )}
                                    </Paper>
                                    )}
                                    
                                    {/* Reviewed Violation - Right (with inline editing) */}
                                    {!hideReviewed && (
                                    <Paper sx={{ 
                                      flex: 1, 
                                      p: 2, 
                                      border: hasChanges ? '2px solid' : '1px solid', 
                                      borderColor: hasChanges ? 'success.main' : 'divider', 
                                      bgcolor: 'background.paper', 
                                      display: 'flex', 
                                      flexDirection: 'column' 
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                          {displayViolation?.RuleID || 'Unknown Rule'}
                                        </Typography>
                                        <Chip 
                                          label={displayViolation?.Scenario || 'Unknown Scenario'} 
                                          size="small" 
                                          variant="outlined"
                                          color="secondary"
                                        />
                                        {isNewViolation && (
                                          <Chip 
                                            label="New" 
                                            size="small" 
                                            color="info"
                                            sx={{ fontWeight: 'bold' }}
                                          />
                                        )}
                                        {displayViolation?.ignore === true && (
                                          <Chip 
                                            label="Ignored (will not appear in report)" 
                                            size="small" 
                                            color="warning"
                                            sx={{ fontWeight: 'bold' }}
                                          />
                                        )}
                                        {hasChanges && !isEditing && (
                                          <>
                                            <Chip 
                                              label="Modified" 
                                              size="small" 
                                              color="success"
                                            />
                                            {displayViolation?.lastModified && (
                                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                                {new Date(displayViolation.lastModified).toLocaleString()}
                                              </Typography>
                                            )}
                                          </>
                                        )}
                                        {allowAddEditDelete && !isEditing && editingCardIndex === null && (
                                          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                                            {isNewViolation && (
                                              <IconButton 
                                                size="small" 
                                                onClick={() => handleDeleteViolation(index)}
                                                disabled={saving}
                                                sx={{ color: 'error.main' }}
                                                title="Delete this violation"
                                              >
                                                <DeleteIcon sx={{ fontSize: 18 }} />
                                              </IconButton>
                                            )}
                                            <IconButton 
                                              size="small" 
                                              onClick={() => handleStartEditCard(index)}
                                              title="Edit this violation"
                                            >
                                              <EditIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                          </Box>
                                        )}
                                      </Box>
                                      
                                      {isEditing ? (
                                        <Box>
                                          {/* Editable fields for new violations */}
                                          {isNewViolation && (
                                            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                                              <FormControl fullWidth size="small" required>
                                                <InputLabel>Rule ID</InputLabel>
                                                <Select
                                                  value={displayViolation?.RuleID || ''}
                                                  onChange={(e) => {
                                                    // Prevent selection if clicking on the checkbox item
                                                    if (e.target.value !== '__checkbox__') {
                                                      handleUpdateRuleID(index, e.target.value);
                                                    }
                                                  }}
                                                  label="Rule ID"
                                                  renderValue={(value) => {
                                                    const isDeceptor = isDeceptorRule(value);
                                                    return (
                                                      <Box component="span" sx={isDeceptor ? { color: 'warning.main' } : {}}>
                                                        {value}
                                                      </Box>
                                                    );
                                                  }}
                                                  MenuProps={{
                                                    PaperProps: {
                                                      sx: {
                                                        '& .MuiMenuItem-root:first-of-type': {
                                                          pointerEvents: 'auto',
                                                          '&:hover': {
                                                            backgroundColor: 'transparent'
                                                          }
                                                        }
                                                      }
                                                    }
                                                  }}
                                                >
                                                  <MenuItem 
                                                    value="__checkbox__" 
                                                    disabled
                                                    sx={{ 
                                                      opacity: 1,
                                                      cursor: 'default',
                                                      '&:hover': { backgroundColor: 'transparent' },
                                                      '&.Mui-disabled': { opacity: 1 }
                                                    }}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setShowDeceptorsOnly(!showDeceptorsOnly);
                                                    }}
                                                  >
                                                    <FormControlLabel
                                                      control={
                                                        <Checkbox
                                                          checked={showDeceptorsOnly}
                                                          onChange={(e) => {
                                                            e.stopPropagation();
                                                            setShowDeceptorsOnly(e.target.checked);
                                                          }}
                                                          size="small"
                                                          onClick={(e) => e.stopPropagation()}
                                                        />
                                                      }
                                                      label={
                                                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                                          Deceptors only
                                                        </Typography>
                                                      }
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowDeceptorsOnly(!showDeceptorsOnly);
                                                      }}
                                                    />
                                                  </MenuItem>
                                                  {getRuleIds().map(ruleId => {
                                                    const isDeceptor = isDeceptorRule(ruleId);
                                                    return (
                                                      <MenuItem 
                                                        key={ruleId} 
                                                        value={ruleId}
                                                        sx={isDeceptor ? { color: 'warning.main' } : {}}
                                                      >
                                                        {ruleId}
                                                      </MenuItem>
                                                    );
                                                  })}
                                                </Select>
                                              </FormControl>
                                              <FormControl 
                                                fullWidth 
                                                size="small" 
                                                required
                                                disabled={!displayViolation?.RuleID}
                                              >
                                                <InputLabel>Scenario</InputLabel>
                                                <Select
                                                  value={displayViolation?.Scenario || ''}
                                                  onChange={(e) => handleUpdateScenario(index, e.target.value)}
                                                  label="Scenario"
                                                >
                                                  {getScenariosForRule(displayViolation?.RuleID).map(scenario => (
                                                    <MenuItem key={scenario} value={scenario}>
                                                      {scenario}
                                                    </MenuItem>
                                                  ))}
                                                </Select>
                                              </FormControl>
                                            </Box>
                                          )}
                                          
                                          {/* Display original summary for reference (only if not new) */}
                                          {!isNewViolation && originalViolation && (
                                            <Box sx={{ 
                                              mb: 1, 
                                              p: 1.5, 
                                              bgcolor: 'action.hover', 
                                              borderRadius: 1,
                                              border: '1px solid',
                                              borderColor: 'divider'
                                            }}>
                                              <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                Original Summary:
                                              </Typography>
                                              <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.5, color: 'text.primary' }}>
                                                {originalViolation.ViolationSummary || 'No original summary available.'}
                                              </Typography>
                                            </Box>
                                          )}
                                          
                                          {/* Editable text field */}
                                          <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            value={displayViolation?.ViolationSummary || ''}
                                            onChange={(e) => handleUpdateSummary(index, e.target.value)}
                                            variant="outlined"
                                            size="small"
                                            placeholder={isNewViolation ? "Enter violation summary..." : "Enter revised violation summary..."}
                                            label={isNewViolation ? "Violation Summary" : "Revised Summary"}
                                            sx={{ mb: 2 }}
                                            required={isNewViolation}
                                          />
                                        </Box>
                                      ) : (
                                        <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem', lineHeight: 1.5 }}>
                                          {displayViolation?.ViolationSummary || 'No violation summary available.'}
                                        </Typography>
                                      )}
                                      
                                      <Box>
                                        {displayViolation?.EvidenceFileNames && displayViolation.EvidenceFileNames.length > 0 && (
                                          <Box sx={{ mb: 1 }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                              {displayViolation.EvidenceFileNames.map((fileName, idx) => (
                                                <Box key={idx} sx={{ 
                                                  width: 120, 
                                                  height: 90, 
                                                  flexShrink: 0,
                                                  position: 'relative'
                                                }}>
                                                  <AutoInspectImage 
                                                    imagePath={fileName} 
                                                    appInfo={appInfo}
                                                    alt={`Evidence ${idx + 1} for ${displayViolation.RuleID}`}
                                                    width={120}
                                                    height={90}
                                                  />
                                                  {isEditing && (
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => handleRemoveEvidence(index, idx)}
                                                      sx={{
                                                        position: 'absolute',
                                                        top: -8,
                                                        right: -8,
                                                        bgcolor: 'error.main',
                                                        color: 'white',
                                                        '&:hover': { bgcolor: 'error.dark' },
                                                        width: 24,
                                                        height: 24
                                                      }}
                                                    >
                                                      <DeleteIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                  )}
                                                </Box>
                                              ))}
                                            </Box>
                                          </Box>
                                        )}
                                        {isEditing && (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={() => handleOpenImageDialog(index)}
                                            sx={{ mt: displayViolation?.EvidenceFileNames && displayViolation.EvidenceFileNames.length > 0 ? 0 : 0 }}
                                          >
                                            {displayViolation?.EvidenceFileNames && displayViolation.EvidenceFileNames.length > 0 ? 'Manage Images' : 'Add Images'}
                                          </Button>
                                        )}
                                      </Box>
                                      
                                      {/* Ignore checkbox - shown when editing */}
                                      {isEditing && (
                                        <Box sx={{ mb: 2 }}>
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={displayViolation?.ignore === true}
                                                onChange={() => handleToggleIgnore(index)}
                                                color="warning"
                                              />
                                            }
                                            label={
                                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                                Ignore this violation (will not appear in report)
                                              </Typography>
                                            }
                                          />
                                        </Box>
                                      )}
                                      
                                      {/* Edit buttons - shown below images when in edit mode */}
                                      {isEditing && (
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                                          {isNewViolation && (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              color="error"
                                              startIcon={<DeleteIcon />}
                                              onClick={() => handleDeleteViolation(index)}
                                              disabled={saving}
                                              sx={{ mr: 'auto' }}
                                            >
                                              Delete
                                            </Button>
                                          )}
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleCancelEditCard}
                                            disabled={saving}
                                          >
                                            Cancel
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                            onClick={handleSaveEditCard}
                                            disabled={saving}
                                          >
                                            {saving ? 'Saving...' : 'Save'}
                                          </Button>
                                        </Box>
                                      )}
                                    </Paper>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            /* Show full-width original violations when no review exists */
                            autoinspectData?.summarizedInfo?.results && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {autoinspectData.summarizedInfo.results.map((violation, index) => (
                                <Paper key={index} sx={{ p: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                      {violation.RuleID || 'Unknown Rule'}
                                    </Typography>
                                    <Chip 
                                      label={violation.Scenario || 'Unknown Scenario'} 
                                      size="small" 
                                      variant="outlined"
                                      color="secondary"
                                    />
                                  </Box>
                                  <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem', lineHeight: 1.5 }}>
                                    {violation.ViolationSummary || 'No violation summary available.'}
                                  </Typography>
                                  {violation.EvidenceFileNames && violation.EvidenceFileNames.length > 0 && (
                                    <Box>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {violation.EvidenceFileNames.map((fileName, idx) => (
                                          <Box key={idx} sx={{ width: 120, height: 90, flexShrink: 0 }}>
                                            <AutoInspectImage 
                                              imagePath={fileName} 
                                              appInfo={appInfo}
                                              alt={`Evidence ${idx + 1} for ${violation.RuleID}`}
                                              width={120}
                                              height={90}
                                            />
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                </Paper>
                              ))}
                            </Box>
                            )
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No violation summary data available.
                        </Typography>
                      )}
            </Box>
          )}
                </Box>
            </Box>
          )}
    </>
  );

  const subDialogs = (
    <>
      {/* Image Selection Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={handleCloseImageDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { width: '90vw', height: '80vh', maxWidth: 'none', maxHeight: 'none' } }}
      >
        <DialogTitle>
          Select Images for Violation
          {imageDialogViolationIndex !== null && editingViolations[imageDialogViolationIndex] && (
            <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 'normal', ml: 1 }}>
              ({editingViolations[imageDialogViolationIndex].RuleID || 'Unknown Rule'})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ height: 'calc(80vh - 120px)', overflowY: 'auto' }}>
          {mouseClickRowsWithImages && mouseClickRowsWithImages.length > 0 ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select images to include as evidence. Checked images are already included in this violation.
              </Typography>
              <Grid container spacing={2}>
                {(() => {
                  // Get unique click images
                  const availableImages = mouseClickRowsWithImages
                    ?.filter(event => event.clickImageFileName != null)
                    .map(event => ({
                      clickImageFileName: event.clickImageFileName,
                      videoOffset: event.VideoOffset,
                      event: event
                    })) || [];
                  
                  const uniqueImages = Array.from(
                    new Map(availableImages.map(img => [img.clickImageFileName, img])).values()
                  );
                  
                  return uniqueImages.map((imageData) => {
                    const imagePath = getImageStoragePath(imageData.clickImageFileName);
                    const isSelected = selectedImagePaths.includes(imagePath);
                    
                    return (
                      <Grid item key={imageData.clickImageFileName}>
                        <ClickImageDialogItem
                          event={imageData.event}
                          clickImageFileName={imageData.clickImageFileName}
                          appInfo={appInfo}
                          isSelected={isSelected}
                          onToggle={handleToggleImageSelection}
                          videoOffset={imageData.videoOffset}
                        />
                      </Grid>
                    );
                  });
                })()}
              </Grid>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                No click images available.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleApplyImageSelection} 
            variant="contained" 
            color="primary"
          >
            Apply ({selectedImagePaths.length} selected)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Redo Confirmation Dialog */}
      <Dialog
        open={redoConfirmOpen}
        onClose={() => setRedoConfirmOpen(false)}
      >
        <DialogTitle>Redo autoinspect</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will run autoinspect again for this inspection run.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={redoReinspect}
                onChange={(e) => setRedoReinspect(e.target.checked)}
              />
            }
            label="Reinspect: re-run violation checks on the same scenarios"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRedoConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmRedo} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmIndex != null}
        onClose={() => setDeleteConfirmIndex(null)}
      >
        <DialogTitle>{deleteError ? 'Cannot delete' : 'Delete violation'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deleteError || 'Are you sure you want to delete this violation? This cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmIndex(null)}>
            {deleteError ? 'OK' : 'Cancel'}
          </Button>
          {!deleteError && (
            <Button onClick={handleConfirmDelete} variant="contained" color="error">
              Delete
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );

  if (inline) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', p: 1.5 }}>
        {violationsContent}
        {subDialogs}
      </Box>
    );
  }

  return (
    <Box style={style}>
      <Button 
        size="small"
        onClick={handleOpen}
        startIcon={<AssessmentIcon sx={{ fontSize: 18 }} />}
        disabled={loading}
        sx={{ py: 0.5, minWidth: 0, minHeight: 28 }}
      >
        Violations
      </Button>

      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ sx: { width: '80vw', height: '80vh', maxWidth: 'none', maxHeight: 'none' } }}
      >
        <DialogTitle>
          Violations
          {autoinspectData?.summarizeTime && (
            <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 'normal' }}>
              {' '}({new Date(autoinspectData.summarizeTime).toLocaleString()})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ height: 'calc(80vh - 70px)', display: 'flex', flexDirection: 'column', p: 3, overflow: 'hidden' }}>
          {violationsContent}
          <Box mt={2} textAlign="right">
            <Button onClick={handleClose} variant="contained">Close</Button>
          </Box>
        </DialogContent>
      </Dialog>
      {subDialogs}
    </Box>
  );
}
